import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react'
import {
  Field,
  Textarea,
} from '@lightspeed/unified-components-helios-theme/react'
import { useNoteFieldModel, NoteFieldProps } from './useNoteFieldModel'

export const NoteField = forwardRef<HTMLTextAreaElement, NoteFieldProps>(
  (props, ref) => {
    // Intentionally unused props to avoid passing them down to the Textarea component
    const {
      label,
      descriptionSlot,
      autoFocus,
      charLimit: _charLimit,
      onErrorsChange: _onErrorsChange,
      onValidChange: _onValidChange,
      ...rest
    } = props
    const model = useNoteFieldModel(props)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(ref, () => textareaRef.current!, [])

    // useLayoutEffect to focus before the browser paints, avoiding a visible focus delay
    useLayoutEffect(() => {
      if (autoFocus) {
        textareaRef.current?.focus()
      }
    }, [autoFocus])

    return (
      <div className="uc-base">
        <Field
          invalid={!model.isValid}
          descriptionSlot={descriptionSlot}
          labelSlot={
            label ? (
              <span className="typography-body-sm-emphasized">{label}</span>
            ) : undefined
          }
          errorMessageSlot={
            model.errors.length > 0 ? (
              <span className="flex flex-col gap-1">
                {model.errors.map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </span>
            ) : undefined
          }
          hintSlot={
            model.hintMessage ? (
              <span data-testid="note-field-hint">{model.hintMessage}</span>
            ) : undefined
          }
        >
          <Textarea
            ref={textareaRef}
            customClasses={{
              controlContainer: ['max-h-32', 'overflow-auto'],
            }}
            adaptive={true}
            invalid={!model.isValid}
            size="small"
            {...rest}
          />
        </Field>
      </div>
    )
  }
)
